import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ANDROID_STORE_URL,
  IOS_STORE_URL,
  STORE_FALLBACK_MS,
  getDeviceType
} from "../utils/appLinks";

function StoreLink({ href, children }) {
  return (
    <a
      href={href}
      rel="noreferrer"
      className="rounded-lg bg-[#6A0DAD] px-5 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-[#4C087D]"
    >
      {children}
    </a>
  );
}

function navigateTo(url) {
  window.location.href = url;
}

function AppLinkRedirect({
  deepLinkUrl,
  invalid,
  openingTitle,
  invalidTitle,
  invalidDescription = "Please check the link and try again.",
  autoOpen = true,
  openUrl = navigateTo
}) {
  const fallbackTimerRef = useRef();
  const hasAutoOpenedRef = useRef(false);
  const deviceType = getDeviceType();
  const [showFallback, setShowFallback] = useState(
    invalid || deviceType === "other"
  );

  const clearStoreFallback = useCallback(() => {
    window.clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = undefined;
  }, []);

  const scheduleFallback = useCallback(() => {
    fallbackTimerRef.current = window.setTimeout(() => {
      setShowFallback(true);
    }, STORE_FALLBACK_MS);
  }, []);

  const openApp = useCallback(() => {
    clearStoreFallback();

    if (invalid || !deepLinkUrl) {
      setShowFallback(true);
      return;
    }

    setShowFallback(false);
    openUrl(deepLinkUrl);
    scheduleFallback();
  }, [clearStoreFallback, deepLinkUrl, invalid, openUrl, scheduleFallback]);

  useEffect(() => {
    const handleAppOpened = () => {
      if (document.hidden) {
        clearStoreFallback();
      }
    };

    document.addEventListener("visibilitychange", handleAppOpened);
    window.addEventListener("pagehide", clearStoreFallback);

    if (
      autoOpen &&
      deviceType !== "other" &&
      !invalid &&
      !hasAutoOpenedRef.current
    ) {
      hasAutoOpenedRef.current = true;
      openApp();
    } else if (
      autoOpen &&
      deviceType !== "other" &&
      !invalid &&
      hasAutoOpenedRef.current &&
      !fallbackTimerRef.current
    ) {
      scheduleFallback();
    }

    return () => {
      clearStoreFallback();
      document.removeEventListener("visibilitychange", handleAppOpened);
      window.removeEventListener("pagehide", clearStoreFallback);
    };
  }, [
    autoOpen,
    clearStoreFallback,
    deviceType,
    invalid,
    openApp,
    scheduleFallback
  ]);

  const title = invalid
    ? invalidTitle
    : showFallback
      ? "Open in Mondoway"
      : openingTitle;
  const helperText = invalid
    ? invalidDescription
    : showFallback
      ? deviceType === "other"
        ? "Open this link on your phone, or download Mondoway to continue."
        : "If the app did not open, try again or download Mondoway."
      : "If the app is installed, it should open automatically.";

  return (
    <section className="min-h-[60vh] bg-[#FAF8F2] px-4 py-20 text-center">
      <div className="mx-auto max-w-md">
        <img
          src="/icons/new_login_logo.svg"
          alt="Mondoway Logo"
          className="mx-auto mb-8 h-16 w-auto"
        />
        {!invalid && !showFallback && (
          <div
            aria-label="Opening Mondoway"
            className="mx-auto mb-5 h-8 w-8 animate-spin rounded-full border-4 border-[#D8B4FE] border-t-[#6A0DAD]"
          />
        )}
        <h1 className="mb-3 text-2xl font-bold text-[#0F172A]">{title}</h1>
        <p className="text-[#64748B]">{helperText}</p>

        {(invalid || showFallback) && (
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {!invalid && (
              <button
                type="button"
                onClick={openApp}
                className="rounded-lg border border-[#D8B4FE] bg-white px-5 py-3 font-semibold text-[#6A0DAD] shadow-sm transition-colors hover:bg-[#F4E8FF]"
              >
                Open in Mondoway
              </button>
            )}
            <StoreLink href={IOS_STORE_URL}>Download on the App Store</StoreLink>
            <StoreLink href={ANDROID_STORE_URL}>Get it on Google Play</StoreLink>
          </div>
        )}
      </div>
    </section>
  );
}

export default AppLinkRedirect;
